// Parses a Multi-Channel Report's LLM output into its six evidence-model
// sections (docs/CLAUDE_ZMIST_TECHNICAL_PLAN.md §5, backend/internal/report/
// generate.go's buildMultiPrompt). The prompt asks for a strict, machine-
// parseable contract - each section header on its own line as
// "## <header text>", verbatim, in a fixed order - specifically so this
// parser can split on it instead of guessing at natural-language structure.
//
// Only ever call this on a message that's actually a Multi-Channel Report
// (Message.additionalChannelIds non-empty - an ordinary single-channel
// report was never prompted with this format and will correctly fail to
// parse). A report generated before this format existed, or one from an
// LLM that ignored the instruction, also fails to parse - ReportsScreen
// falls back to rendering the raw text in that case, so a malformed report
// never disappears, it just loses its structured layout.
export interface EvidenceReport {
  directAnswer: string;
  keyEvidence: string;
  relationships: string;
  // null when the Confidence section didn't lead with one of the three
  // expected words - the raw section text still renders as
  // confidenceReason in that case, just without a colored badge.
  confidenceLevel: 'High' | 'Medium' | 'Low' | null;
  confidenceReason: string;
  conflictingEvidence: string;
  nextQuestion: string;
}

const SECTION_HEADERS = [
  'Direct answer',
  'Key evidence',
  'Relationships across channels',
  'Confidence',
  'Conflicting evidence',
  'Suggested next question',
] as const;

const HEADER_LINE_RE = /^##\s+(.+?)\s*$/gm;

// Shared by parseEvidenceReport and parseStandardReport below: finds each
// of `headers`, in order, as its own "## <header>" line, and returns a
// same-length array of that section's trimmed body text - or null if any
// expected header is missing (not a well-formed instance of that prompt's
// structured format; the caller falls back to rendering the raw text).
function splitSections(text: string, headers: readonly string[]): string[] | null {
  const headerMatches = [...text.matchAll(HEADER_LINE_RE)];
  if (headerMatches.length === 0) return null;

  let searchFrom = 0;
  const positions: { start: number; contentStart: number }[] = [];
  for (const header of headers) {
    const match = headerMatches.find(
      (m) => m.index !== undefined && m.index >= searchFrom && m[1].trim().toLowerCase() === header.toLowerCase()
    );
    if (!match || match.index === undefined) return null;
    positions.push({ start: match.index, contentStart: match.index + match[0].length });
    searchFrom = match.index + 1;
  }

  return positions.map((p, i) => {
    const end = i + 1 < positions.length ? positions[i + 1].start : text.length;
    return text.slice(p.contentStart, end).trim();
  });
}

export function parseEvidenceReport(text: string): EvidenceReport | null {
  const sections = splitSections(text, SECTION_HEADERS);
  if (!sections) return null;
  const sectionText = (i: number) => sections[i];

  const confidenceRaw = sectionText(3);
  const levelMatch = confidenceRaw.match(/^(High|Medium|Low)\b\s*[:.,–-]?\s*/i);
  const confidenceLevel = levelMatch
    ? ((levelMatch[1][0].toUpperCase() + levelMatch[1].slice(1).toLowerCase()) as EvidenceReport['confidenceLevel'])
    : null;
  const confidenceReason = levelMatch ? confidenceRaw.slice(levelMatch[0].length).trim() : confidenceRaw;

  return {
    directAnswer: sectionText(0),
    keyEvidence: sectionText(1),
    relationships: sectionText(2),
    confidenceLevel,
    confidenceReason,
    conflictingEvidence: sectionText(4),
    nextQuestion: sectionText(5),
  };
}

// resolveConfidenceLevel prefers the backend's code-computed confidence
// (Message.confidence - internal/report/generate.go's computeConfidence,
// CLAUDE_ZMIST_TECHNICAL_PLAN.md §5.4) over the LLM's own self-assessed
// level parsed out of its "## Confidence" text. The plan's own discipline
// point is that the label should come from computed factors, not from
// whatever the LLM feels like saying - the parsed level is kept only as a
// fallback for a report generated before the `confidence` field existed
// (or, in principle, one from a codepath that never set it).
export function resolveConfidenceLevel(
  computed: string | undefined,
  parsedLevel: EvidenceReport['confidenceLevel']
): EvidenceReport['confidenceLevel'] {
  if (computed === 'High' || computed === 'Medium' || computed === 'Low') return computed;
  return parsedLevel;
}

// Parses a single-channel report's LLM output (internal/report/prompts.go's
// PromptStandard) into its six sections - ZMIST_PUBLIC_CHANNELS_PLAN.md §5's
// structure, not the Multi-Channel Report's six sections above (different
// headers, different prompt). Same "## <header>" strict-contract parsing;
// a report generated before this format shipped, or a channel-level prompt
// override, fails to parse and the caller falls back to the raw text.
export interface StandardReport {
  summary: string;
  whatChanged: string;
  whyItMayMatter: string;
  possibleCauses: string;
  evidence: string;
  whatToWatchNext: string;
}

const STANDARD_SECTION_HEADERS = [
  'Summary',
  'What changed',
  'Why it may matter',
  'Possible causes',
  'Evidence',
  'What to watch next',
] as const;

export function parseStandardReport(text: string): StandardReport | null {
  const sections = splitSections(text, STANDARD_SECTION_HEADERS);
  if (!sections) return null;
  return {
    summary: sections[0],
    whatChanged: sections[1],
    whyItMayMatter: sections[2],
    possibleCauses: sections[3],
    evidence: sections[4],
    whatToWatchNext: sections[5],
  };
}

export interface GenericSection {
  heading: string;
  body: string;
}

// Renders an owner-edited report (see dataApi.ts's updateReport) that no
// longer matches either fixed 6-header contract above - once a human can
// edit a report's markdown, they can rename, add, drop, or reorder
// sections freely (the whole point of "any amount of sections" instead
// of a hardcoded template), so there's no fixed name list to parse
// against. Every "## Heading" line found, in document order, each
// paired with the text up to the next heading (or end of document).
// Returns null when the text has no "## " headers at all (plain prose,
// or a pre-structured-format report) - the caller falls back further to
// raw MarkdownReportText in that case.
export function parseGenericSections(text: string): GenericSection[] | null {
  const headerMatches = [...text.matchAll(HEADER_LINE_RE)];
  if (headerMatches.length === 0) return null;
  return headerMatches.map((match, i) => {
    const contentStart = (match.index ?? 0) + match[0].length;
    const end = i + 1 < headerMatches.length ? (headerMatches[i + 1].index ?? text.length) : text.length;
    return { heading: match[1].trim(), body: text.slice(contentStart, end).trim() };
  });
}
